import { Label } from '@/shared/components/ui/label';
import { MultiSelectCombobox } from '@/shared/components/ui/multi-select-combobox';
import {
  CLIENT_SEGMENT_OPTIONS,
  INVESTABLE_ASSETS_OPTIONS,
  SERVICE_OFFERING_OPTIONS,
  type ClientSegment,
  type FpProfileOption,
  type InvestableAssetsRange,
  type ServiceOffering,
} from '@/shared/constants/fpProfileOptions';

/**
 * Shared editor for the Feature 074 structured FP profile fields. Used by both the
 * FP self-edit form and the admin advisor editor so the controls and behavior
 * (multi-selects, controlled Investable Assets, Service Offerings re-pick) stay
 * consistent across surfaces.
 */
type FpStructuredProfileFieldsProps = {
  clientSegments: ClientSegment[];
  investableAssets: InvestableAssetsRange | '';
  serviceOfferings: ServiceOffering[];
  /** Legacy free-text offerings present on the record but not yet re-picked. */
  hasLegacyServiceOfferings?: boolean;
  serviceOfferingsError?: string;
  disabled?: boolean;
  onClientSegmentsChange: (value: ClientSegment[]) => void;
  onInvestableAssetsChange: (value: InvestableAssetsRange | '') => void;
  onServiceOfferingsChange: (value: ServiceOffering[]) => void;
};

const toComboOptions = <T extends string>(options: ReadonlyArray<FpProfileOption<T>>) =>
  options.map((option) => ({ id: option.value, name: option.label }));

const SELECT_CLASS =
  'mt-1 block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ' +
  'focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:cursor-not-allowed disabled:opacity-50';

export const FpStructuredProfileFields = ({
  clientSegments,
  investableAssets,
  serviceOfferings,
  hasLegacyServiceOfferings = false,
  serviceOfferingsError,
  disabled = false,
  onClientSegmentsChange,
  onInvestableAssetsChange,
  onServiceOfferingsChange,
}: FpStructuredProfileFieldsProps) => {
  const showLegacyNotice = hasLegacyServiceOfferings && serviceOfferings.length === 0;

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="serviceOfferingsList">Service Offerings</Label>
        {showLegacyNotice && (
          <p className="mb-1 text-sm text-amber-700" role="note">
            Select from the list below. Saving will replace your previous free-text entry.
          </p>
        )}
        <MultiSelectCombobox
          options={toComboOptions(SERVICE_OFFERING_OPTIONS)}
          value={serviceOfferings}
          onChange={(value) => onServiceOfferingsChange(value as ServiceOffering[])}
          placeholder="Select service offerings"
          searchPlaceholder="Search offerings"
          disabled={disabled}
          error={serviceOfferingsError}
        />
      </div>

      <div>
        <Label htmlFor="predominantClientSegments">Predominant Client Segments Served</Label>
        <MultiSelectCombobox
          options={toComboOptions(CLIENT_SEGMENT_OPTIONS)}
          value={clientSegments}
          onChange={(value) => onClientSegmentsChange(value as ClientSegment[])}
          placeholder="Select client segments"
          searchPlaceholder="Search segments"
          disabled={disabled}
        />
      </div>

      <div>
        <Label htmlFor="investableAssets">Investable Assets</Label>
        <select
          id="investableAssets"
          className={SELECT_CLASS}
          value={investableAssets}
          onChange={(event) =>
            onInvestableAssetsChange(event.target.value as InvestableAssetsRange | '')
          }
          disabled={disabled}
        >
          <option value="">Select a range (optional)</option>
          {INVESTABLE_ASSETS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
